import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FUENTE, useTema } from './tema';

/**
 * SelectorDePersona — elegir a quién se le asigna algo, buscando.
 *
 * Un `<select>` obliga a recorrer la lista con la vista. Con cuatro compañeros
 * da igual; con los cuarenta de un equipo grande, encontrar a alguien es hacer
 * scroll dentro de un desplegable leyendo nombre por nombre — y en un plan de
 * Planner esa lista es todo el grupo de Microsoft 365 dueño del plan, mucha más
 * gente que la que fue a la reunión.
 *
 * ── Tres decisiones ─────────────────────────────────────────────────────
 *
 * **La caja de búsqueda sale siempre**, aunque haya tres personas. Aparecer
 * solo a partir de cierto número hace que quien la usó ayer en un equipo la
 * busque hoy en otro y crea que se ha roto. Una caja de más no molesta a nadie;
 * una que va y viene, sí.
 *
 * **Se busca también por correo, y se enseña.** Es lo que resuelve el caso que
 * de verdad cuesta: dos personas que se llaman igual. El nombre no las
 * distingue y el correo sí, y si no está a la vista hay que salir a otra parte
 * a averiguarlo.
 *
 * **Quien no es del equipo sale, pero aparte.** La lista puede traer a toda la
 * empresa, y Planner solo acepta asignar a la gente del grupo dueño del plan.
 * Esconder al resto sería contestar que no existe; mezclarlo sería dejar que
 * alguien elija por orden alfabético a una persona a la que la asignación va a
 * fallar. Van en dos bloques, con el del equipo primero y el otro rotulado.
 *
 * **La lista se pinta fuera, en el `body`.** Esto no es un detalle de estilo.
 * Un `<select>` nativo dibuja su desplegable por encima de todo porque lo pinta
 * el sistema, no la página; una lista hecha a mano vive dentro del documento y
 * la recorta el primer antepasado con `overflow` distinto de `visible`. Aquí
 * hay dos: el recuadro de la tabla (`overflow: hidden`, para las esquinas
 * redondeadas) y el cuerpo del diálogo (`overflowY: auto`). Dentro, la lista
 * habría salido cortada a la altura de la fila — o no habría salido. Por eso va
 * en un portal y se posiciona con las coordenadas del botón.
 */

/** Sin acentos ni mayúsculas: quien busca a Muñoz escribe «munoz». */
function llano(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().trim();
}

const ANCHO_PANEL = 264;
const ALTO_PANEL = 290;
// La lista no se estira sin fin: un desplegable de media pantalla para leer
// nombres de tres palabras es peor que uno de la medida de la lectura.
const ANCHO_MAXIMO = 420;

const estilos = (C) => ({
  caja: { position: 'relative', maxWidth: 220 },

  boton: {
    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    padding: '4px 7px', borderRadius: 6, cursor: 'pointer', textAlign: 'left',
    fontSize: 12.5, fontFamily: FUENTE,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`
  },
  nombreBoton: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sinNadie: { color: C.tenue },
  puntaFlecha: { color: C.tenue, fontSize: 10, flexShrink: 0 },

  panel: {
    position: 'fixed', zIndex: 120, width: ANCHO_PANEL, padding: 5, borderRadius: 9,
    background: C.panelAlto, border: `1px solid ${C.borde}`,
    boxShadow: '0 12px 34px rgba(0,0,0,.42)'
  },
  buscador: {
    width: '100%', padding: '6px 9px', marginBottom: 5, borderRadius: 6,
    fontSize: 12.5, fontFamily: FUENTE, boxSizing: 'border-box',
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`
  },
  lista: { maxHeight: 216, overflowY: 'auto' },
  opcion: (resaltada) => ({
    display: 'block', width: '100%', padding: '6px 9px', borderRadius: 6,
    cursor: 'pointer', textAlign: 'left', border: 'none',
    background: resaltada ? C.panel : 'transparent',
    color: C.texto, fontSize: 12.5, fontFamily: FUENTE, lineHeight: 1.35
  }),
  correo: { display: 'block', fontSize: 11, color: C.tenue, marginTop: 1 },
  rotulo: {
    padding: '7px 9px 4px', fontSize: 10.5, fontWeight: 600, letterSpacing: .3,
    color: C.tenue, textTransform: 'uppercase'
  },
  fuera: {
    padding: '4px 9px 7px', fontSize: 11, color: C.atencion, lineHeight: 1.45
  },
  marca: { color: C.acentoClaro, marginLeft: 6 },
  nada: { padding: '8px 9px', fontSize: 12.5, color: C.tenue, lineHeight: 1.5 }
});

/**
 * @param {Object} props
 * @param {Array} props.personas - [{ id, nombre, correo, puesto, enEquipo }]
 * @param {string} props.valor - el id elegido, o '' para nadie
 * @param {Function} props.onCambio - recibe (id, persona). `persona` trae el
 *        nombre y el puesto, que es lo que necesita quien guarda un nombre y no
 *        un identificador.
 * @param {string|null} props.textoMostrado - que enseñar en el boton cuando lo
 *        guardado no es un id del directorio: un nombre escrito a mano, o uno
 *        que venia en el acta.
 * @param {boolean} props.textoLibre - si se admite un nombre que no este en el
 *        directorio. A una reunion va gente de fuera de la empresa, y una lista
 *        cerrada dejaria sin manera de apuntarla.
 * @param {Function|null} props.interpretarTexto - que hacer con lo tecleado
 *        cuando no encaja con nadie. Recibe el texto y devuelve una persona,
 *        `{ aviso }` para decir por que no vale, o null. Sin esto, lo tecleado
 *        se toma como un nombre a secas, que es lo que quiere Planner y NO lo
 *        que quiere una ronda de firmas: alli hace falta el correo.
 * @param {string} props.vacio - como se llama «nadie» aqui.
 * @param {string} props.etiqueta - para quien navegue a ciegas
 * @param {Object|null} props.estilo - el del campo, cuando esto no va en una
 *        celda de tabla sino en la cabecera del acta, donde tiene que medir
 *        como las demas casillas y no como un boton pequeño.
 */
const SelectorDePersona = ({
  personas = [], valor = '', onCambio, etiqueta = 'Se le asigna a',
  textoMostrado = null, textoLibre = false, vacio = 'Sin asignar', estilo = null,
  interpretarTexto = null
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resaltada, setResaltada] = useState(0);
  const [sitio, setSitio] = useState(null);

  const disparador = useRef(null);
  const panel = useRef(null);

  const elegida = personas.find(p => p.id === valor) || null;
  // Lo que se lee en el boton. Un nombre escrito a mano no esta en el
  // directorio y no tiene id, pero sigue siendo lo que hay guardado.
  const enElBoton = elegida ? elegida.nombre : (textoMostrado || '');

  const filtradas = useMemo(() => {
    const texto = llano(busqueda);
    if (!texto) return personas;
    // Por cualquier parte del nombre o del correo: quien busca a «Muñoz
    // Bojorquez» escribe «bojor», no «Armando Alfonso».
    return personas.filter(
      p => llano(p.nombre).includes(texto) || llano(p.correo).includes(texto)
    );
  }, [personas, busqueda]);

  // Se parte en dos: el equipo del plan y el resto. Quien viene de fuera no se
  // esconde —se pidió poder verlo— pero tampoco se mezcla.
  const { delEquipo, deFuera } = useMemo(() => ({
    delEquipo: filtradas.filter(p => p.enEquipo !== false),
    deFuera: filtradas.filter(p => p.enEquipo === false)
  }), [filtradas]);

  // Lo tecleado, cuando no coincide con nadie y se admiten nombres de fuera.
  // Va el primero: quien escribe el nombre entero de un invitado externo lo que
  // quiere es usarlo, no seguir buscando.
  const aPelo = useMemo(() => {
    if (!textoLibre) return null;
    const texto = busqueda.trim();
    if (!texto) return null;
    if (filtradas.some(p => llano(p.nombre) === llano(texto))) return null;
    // Quien use esto puede querer otra cosa de lo tecleado.
    //
    // Para un responsable de Planner basta el nombre: queda escrito en el acta
    // y no hay tarea que crear. Para un firmante hace falta el correo, porque
    // el correo ES la peticion de firma. Dos reglas distintas para la misma
    // caja, asi que la regla la pone quien monta el selector y aqui solo se
    // pinta lo que diga.
    if (interpretarTexto) return interpretarTexto(texto);
    return { id: '', nombre: texto, correo: '', puesto: '', aMano: true };
  }, [textoLibre, busqueda, filtradas, interpretarTexto]);

  // Lo tecleado no vale, y hay algo que explicar. No es una opcion: no se puede
  // elegir, no la recorren las flechas y no cuenta para «no se encontro a
  // nadie». Es una linea que dice que falta.
  const reparo = aPelo && aPelo.aviso ? aPelo.aviso : null;
  const elegible = aPelo && !aPelo.aviso ? aPelo : null;

  // «Sin asignar» va siempre y no se filtra: es la salida, y quien se equivocó
  // al elegir viene a por ella, no a buscarla.
  //
  // El orden de esta lista es el que recorren las flechas del teclado, así que
  // tiene que ser el mismo que se pinta abajo.
  const opciones = useMemo(
    () => [
      ...(elegible ? [elegible] : []),
      { id: '', nombre: vacio, correo: '', vacia: true },
      ...delEquipo,
      ...deFuera
    ],
    [elegible, vacio, delEquipo, deFuera]
  );

  // Dónde empieza el segundo bloque, para poner el rótulo justo antes.
  // Cuenta las filas ELEGIBLES de arriba, que son las que recorren las
  // flechas. El reparo no es una de ellas: se pinta aparte.
  const cabecera = (elegible ? 1 : 0) + 1;
  const primeroDeFuera = deFuera.length > 0 ? cabecera + delEquipo.length : -1;

  /** Dónde cae la lista, en coordenadas de ventana. */
  const medir = useCallback(() => {
    const b = disparador.current;
    if (!b) return;
    const r = b.getBoundingClientRect();

    // Si no cabe debajo se abre hacia arriba. Sin esto, las filas del final de
    // la tabla abrirían su lista fuera de la pantalla.
    const hueco = window.innerHeight - r.bottom;
    const arriba = hueco < ALTO_PANEL && r.top > hueco;

    // La lista mide al menos lo que su campo. En una celda de tabla el boton es
    // estrecho y la lista necesita mas, para que quepan el nombre y el correo;
    // en la cabecera del acta el campo ocupa media fila, y una lista mucho mas
    // angosta que el campo del que cuelga parece de otro sitio.
    const ancho = Math.max(ANCHO_PANEL, Math.min(Math.round(r.width), ANCHO_MAXIMO));

    setSitio({
      ancho,
      izquierda: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)),
      arriba: arriba ? undefined : r.bottom + 3,
      abajo: arriba ? window.innerHeight - r.top + 3 : undefined
    });
  }, []);

  useEffect(() => { setResaltada(0); }, [busqueda]);

  // Al cerrarse se olvida lo tecleado: reabrirlo con el filtro de la vez
  // anterior puesto enseña media lista y parece que faltan personas.
  useEffect(() => { if (!abierto) setBusqueda(''); }, [abierto]);

  useEffect(() => {
    if (!abierto) return undefined;
    medir();

    const fuera = (e) => {
      const dentroDelBoton = disparador.current && disparador.current.contains(e.target);
      // El panel está en el `body` y no dentro de esta caja, así que hay que
      // preguntarle a él aparte: si no, pulsar una opción se leería como pulsar
      // fuera y el desplegable se cerraría antes de elegir nada.
      const dentroDelPanel = panel.current && panel.current.contains(e.target);
      if (!dentroDelBoton && !dentroDelPanel) setAbierto(false);
    };

    // La lista está anclada a la ventana, así que si lo de debajo se mueve hay
    // que volver a medir o se queda flotando sobre otra fila.
    const seMovio = () => medir();

    document.addEventListener('mousedown', fuera);
    window.addEventListener('resize', seMovio);
    window.addEventListener('scroll', seMovio, true);
    return () => {
      document.removeEventListener('mousedown', fuera);
      window.removeEventListener('resize', seMovio);
      window.removeEventListener('scroll', seMovio, true);
    };
  }, [abierto, medir]);

  const elegir = (persona) => {
    onCambio(persona.id, persona.vacia ? null : persona);
    setAbierto(false);
    if (disparador.current) disparador.current.focus();
  };

  const alPulsarTecla = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setAbierto(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setResaltada(i => Math.min(i + 1, opciones.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltada(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const o = opciones[resaltada];
      if (o) elegir(o);
    }
  };

  const lista = (
    <div
      ref={panel}
      role="listbox"
      style={{
        ...S.panel,
        width: sitio ? sitio.ancho : ANCHO_PANEL,
        left: sitio ? sitio.izquierda : -9999,
        ...(sitio && sitio.arriba !== undefined ? { top: sitio.arriba } : {}),
        ...(sitio && sitio.abajo !== undefined ? { bottom: sitio.abajo } : {})
      }}
    >
      <input
        style={S.buscador}
        value={busqueda}
        autoFocus
        placeholder="Buscar persona…"
        onChange={(e) => setBusqueda(e.target.value)}
        onKeyDown={alPulsarTecla}
        aria-label="Buscar en el equipo"
      />

      <div style={S.lista}>
        {/* Lo escrito no sirve, y se dice por que. Va encima de la lista y no
            en lugar de ella: quien escribio un nombre sin correo quiza quiera
            seguir mirando a ver si estaba en el directorio despues de todo. */}
        {reparo && <div style={S.nada}>{reparo}</div>}

        {opciones.length === 1 && busqueda && !reparo ? (
          <div style={S.nada}>Nadie coincide con «{busqueda}».</div>
        ) : (
          opciones.map((p, i) => (
            <React.Fragment key={p.id || 'sin-asignar'}>
              {i === primeroDeFuera && (
                <>
                  <div style={S.rotulo}>Fuera de este equipo</div>
                  <div style={S.fuera}>
                    Planner no deja asignarles tareas de este plan. Si eliges a alguien de
                    aquí, la tarea se creará sin responsable.
                  </div>
                </>
              )}
              <button
                type="button"
                style={S.opcion(i === resaltada)}
                role="option"
                aria-selected={p.id === valor}
                onMouseEnter={() => setResaltada(i)}
                onClick={() => elegir(p)}
              >
                <span style={p.vacia ? S.sinNadie : undefined}>
                  {p.aMano ? `Usar «${p.nombre}»` : p.nombre}
                </span>
                {!p.aMano && p.id === valor && <span style={S.marca} aria-hidden="true">✓</span>}
                {/* De uno escrito a mano se enseña su correo cuando lo hay.
                    En una ronda de firmas el correo no es un dato de apoyo: es
                    a donde va la peticion, y es lo unico que se puede revisar
                    antes de mandarla. En Planner no hay correo y se lee igual
                    que antes. */}
                {p.aMano
                  ? (
                    <span style={S.correo}>
                      {p.correo ? `${p.correo} · de fuera` : 'no está en el directorio'}
                    </span>
                  )
                  : (p.correo && <span style={S.correo}>{p.correo}</span>)}
              </button>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );

  return (
    // El tope de 220 es para una celda de tabla. Cuando se le da el estilo del
    // campo, esto es una casilla de la cabecera y mide lo que mida su columna.
    <div style={{ ...S.caja, ...(estilo ? { maxWidth: 'none' } : null) }}>
      <button
        ref={disparador}
        type="button"
        style={{ ...S.boton, ...(estilo || null) }}
        onClick={() => setAbierto(a => !a)}
        onKeyDown={(e) => { if (e.key === 'Escape') setAbierto(false); }}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-label={etiqueta}
        title={elegida ? `${elegida.nombre}${elegida.correo ? ` · ${elegida.correo}` : ''}` : (enElBoton || vacio)}
      >
        <span style={{ ...S.nombreBoton, ...(enElBoton ? {} : S.sinNadie) }}>
          {enElBoton || vacio}
        </span>
        <span style={S.puntaFlecha} aria-hidden="true">▾</span>
      </button>

      {abierto && createPortal(lista, document.body)}
    </div>
  );
};

export default SelectorDePersona;
